import type { ComponentChildren } from 'preact';
import { navigate } from '../router';
import { useIsWide } from '../lib/useIsWide';
import { toasts, dismissToast } from '../store/toast';

type Tab = 'plan' | 'recipes' | 'settings';

export function Layout(props: {
  active: Tab;
  slug: string;
  children: ComponentChildren;
  rightActions?: ComponentChildren;
}) {
  const tabs: { key: Tab; label: string; icon: string; href: string }[] = [
    { key: 'recipes', label: 'Recipes', icon: '🥗', href: `/h/${props.slug}/recipes` },
    { key: 'plan',    label: 'Plan',    icon: '📅', href: `/h/${props.slug}/plan` },
    { key: 'settings', label: 'Settings', icon: '⚙️', href: `/h/${props.slug}/settings` },
  ];
  const isWide = useIsWide();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)' }}>
      {isWide && (
        <header style={{
          padding: '14px 24px', background: 'var(--canvas)',
          borderBottom: '1px solid var(--hairline)',
          display: 'flex', alignItems: 'center', gap: 18,
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <a
            href={`/h/${props.slug}/recipes`}
            onClick={(e) => { e.preventDefault(); navigate(`/h/${props.slug}/recipes`); }}
            style={{
              display: 'inline-flex', alignItems: 'center',
              textDecoration: 'none', cursor: 'pointer',
            }}
            aria-label="MealPlan — go to recipes"
          >
            <img src="/logo.png" alt="MealPlan" style={{ height: 26, width: 'auto', display: 'block' }} />
          </a>
          <nav style={{ display: 'flex', gap: 2, marginLeft: 20 }}>
            {tabs.map(t => (
              <a key={t.key} href={t.href} onClick={(e) => { e.preventDefault(); navigate(t.href); }} style={{
                padding: '8px 14px', fontSize: 14, fontWeight: 500,
                color: t.key === props.active ? 'var(--ink)' : 'var(--muted)',
                textDecoration: 'none', borderRadius: 'var(--radius-pill)',
                background: t.key === props.active ? 'var(--surface-card)' : 'transparent',
              }}>{t.label}</a>
            ))}
          </nav>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {props.rightActions}
          </div>
        </header>
      )}
      <main style={{ paddingBottom: isWide ? 0 : 70 }}>{props.children}</main>
      {!isWide && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'white', borderTop: '1px solid var(--line)',
          display: 'flex', padding: '6px 4px',
        }}>
          {tabs.map(t => (
            <a key={t.key} href={t.href} onClick={(e) => { e.preventDefault(); navigate(t.href); }} style={{
              flex: 1, padding: 6, textAlign: 'center', textDecoration: 'none',
              fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
              color: t.key === props.active ? 'var(--pink-deep)' : 'var(--plum-soft)',
            }}>
              <div style={{ fontSize: 18 }}>{t.icon}</div>
              {t.label}
            </a>
          ))}
        </nav>
      )}
      {toasts.value.length > 0 && (
        <div role="region" aria-label="Notifications" style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', gap: 8, zIndex: 1000,
          pointerEvents: 'none',
        }}>
          {toasts.value.map(t => (
            <div key={t.id} role="status" aria-live="polite" style={{
              background: 'var(--ink)', color: 'white', padding: '10px 16px',
              borderRadius: 'var(--radius-md)', fontSize: 14,
              display: 'flex', alignItems: 'center', gap: 12,
              boxShadow: 'var(--shadow-lift)',
              pointerEvents: 'auto',
            }}>
              <span>{t.message}</span>
              {t.action && (
                <button onClick={() => { t.action!.onClick(); dismissToast(t.id); }} style={{
                  background: 'transparent', color: 'var(--accent-mint)',
                  border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                }}>{t.action.label}</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
